import { useState, useEffect } from 'react';
import * as Joi from 'joi';

import { useForm, useFieldArray } from 'react-hook-form';
import { Stack, IconButton } from '@mui/material';
import { joiResolver } from '@hookform/resolvers/joi';

import { Button } from 'app-zephyr-components/Button';
import { FormInput } from 'app-zephyr-forms';
import { DoubleCopyIcon, DeleteIcon } from 'app-zephyr-icons';
import { AddIcon } from 'app-zephyr-icons/Add';
import { CheckmarkIcon } from 'app-zephyr-icons/Checkmark';
import { Empty } from 'app-zephyr-components/AlertContent';

import { useStyles } from './styles';

// todo: @sveta should be moved into updated ze-api-contract
export const formSchema = Joi.object({
  variables: Joi.array()
    .items(
      Joi.object({
        value: Joi.string().required(),
        key: Joi.string().required(),
      }),
    )
    .optional(),
  draftVariables: Joi.array()
    .items(
      Joi.object({
        value: Joi.string().required().messages({ 'string.empty': '' }),
        key: Joi.string().required().messages({ 'string.empty': '' }),
      }),
    )
    .required(),
});

export interface VariableItem {
  key: string;
  value: string;
}

type EnvironmentVariablesFormFields = {
  draftVariables: VariableItem[] | [];
  variables: VariableItem[];
} & Record<string, string>;

interface EnvironmentVariablesFormProps {
  onSubmit: (value: VariableItem) => void;
  variables: VariableItem[] | [];
  onDelete: (value: VariableItem) => void;
}

const EnvironmentVariablesForm = ({ variables, onSubmit, onDelete }: EnvironmentVariablesFormProps) => {
  const [isCopied, setIsCopied] = useState<{ type: 'default' | 'draft'; index: number } | null>(null);
  const {
    register,
    control,
    getValues,
    reset,
    formState: { isValid, errors, isDirty },
  } = useForm<EnvironmentVariablesFormFields>({
    defaultValues: {
      variables: [...variables],
      draftVariables: [],
    } as EnvironmentVariablesFormFields,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: joiResolver(formSchema),
  });

  useEffect(() => {
    reset({
      variables: [...variables],
      draftVariables: [],
    } as EnvironmentVariablesFormFields);
  }, [variables, reset]);

  const { fields, append, remove } = useFieldArray<EnvironmentVariablesFormFields>({
    control,
    name: 'draftVariables' as never,
  });

  const { fields: variablesDefault } = useFieldArray<EnvironmentVariablesFormFields>({
    control,
    name: 'variables' as never,
  });

  const { classes, theme } = useStyles();

  const onSubmitHandler = () => {
    const data = getValues();
    onSubmit(data.draftVariables[0]);
    reset();
    remove(0);
  };

  const onRemoveDefaultVariable = (index: number) => {
    onDelete(getValues().variables[index]);
  };

  const onAddNewVariable = () => {
    append({ key: '', value: '' });
  };

  const onCopy = (value: VariableItem, index: number, type: 'draft' | 'default') => {
    const str = `${value.key}=${value.value}`;
    void navigator.clipboard.writeText(str);
    setIsCopied({ index, type });

    setTimeout(() => {
      setIsCopied(null);
    }, 2000);
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmitHandler();
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={2}>
          {!!fields.length && (
            <Stack spacing={2}>
              <p className={classes.title}>Action required </p>

              <Stack spacing={1}>
                {fields.map((field, index) => (
                  <div key={field.id + index.toString()}>
                    <Stack direction={'row'} spacing={1} alignItems={'flex-end'}>
                      <Stack direction={'row'} spacing={2} alignItems={'baseline'} width={'100%'}>
                        <FormInput
                          key={`draftVariables-key-${field.id}`}
                          id={`draftVariables.${index.toString()}`}
                          name={`draftVariables.${index.toString()}.key`}
                          register={register}
                          errors={errors}
                          placeholder="Fill variable name"
                          label="Name"
                        />

                        <FormInput
                          key={`draftVariables-value-${field.id}`}
                          id={`draftVariables.${index.toString()}`}
                          name={`draftVariables.${index.toString()}.value`}
                          register={register}
                          errors={errors}
                          placeholder="Fill variable value"
                          label="Value"
                        />
                      </Stack>
                      <Stack direction={'row'}>
                        <IconButton
                          className={classes.iconButton}
                          disableRipple
                          onClick={() => {
                            onCopy(
                              {
                                key: getValues().draftVariables[index].key,
                                value: getValues().draftVariables[index].value,
                              },
                              index,
                              'draft',
                            );
                          }}
                        >
                          {isCopied && isCopied.type === 'draft' && isCopied.index === index ? (
                            <CheckmarkIcon color={theme.palette.tx.success.primary} width={'36px'} />
                          ) : (
                            <DoubleCopyIcon />
                          )}
                        </IconButton>
                        <IconButton
                          className={classes.iconButton}
                          disableRipple
                          onClick={() => {
                            remove(index);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </div>
                ))}
              </Stack>
            </Stack>
          )}

          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <p className={classes.title}>Environment Variables</p>
            <Stack direction={'row'} gap={2}>
              <Button variant="outlined" onClick={onAddNewVariable} disabled={fields.length > 0}>
                <AddIcon
                  color={fields.length > 0 ? '#565656' : theme.palette.tx.secondary.default}
                  className={classes.buttonIcon}
                />{' '}
                New variable
              </Button>
              <Button type="submit" disabled={!isDirty || !isValid}>
                Save
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={1}>
            {!variablesDefault.length && (
              <Empty
                title="It looks like you haven't added any environment variables. Define key-value pairs to configure your application settings"
                isRender={true}
              />
            )}
            {!!variablesDefault.length && (
              <>
                <Stack direction={'row'} spacing={2} alignItems={'baseline'} width={'100%'}>
                  <p style={{ minWidth: '455px' }}>Name</p>
                  <p>Value</p>
                </Stack>
                {variablesDefault.map((field, index) => (
                  <div key={field.id + index.toString()}>
                    <Stack direction={'row'} spacing={1} alignItems={'flex-end'}>
                      <Stack direction={'row'} spacing={2} alignItems={'baseline'} width={'100%'}>
                        <FormInput
                          key={`key-${field.id}`}
                          id={`variables.${index.toString()}`}
                          name={`variables.${index.toString()}.key`}
                          register={register}
                          errors={errors}
                          placeholder="Fill variable name"
                          disabled={true}
                        />

                        <FormInput
                          key={`value-${field.id}`}
                          id={`variables.${index.toString()}`}
                          name={`variables.${index.toString()}.value`}
                          register={register}
                          errors={errors}
                          placeholder="Fill variable value"
                          disabled={true}
                        />
                      </Stack>
                      <Stack direction={'row'}>
                        <IconButton
                          className={classes.iconButton}
                          disableRipple
                          onClick={() => {
                            onCopy(
                              {
                                key: getValues().variables[index].key,
                                value: getValues().variables[index].value,
                              },
                              index,
                              'default',
                            );
                          }}
                        >
                          {isCopied && isCopied.type === 'default' && isCopied.index === index ? (
                            <CheckmarkIcon color={theme.palette.tx.success.primary} width={'36px'} />
                          ) : (
                            <DoubleCopyIcon />
                          )}
                        </IconButton>
                        <IconButton
                          className={classes.iconButton}
                          disableRipple
                          onClick={() => {
                            onRemoveDefaultVariable(index);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </div>
                ))}
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
};

export { EnvironmentVariablesForm };
